import { useEffect } from 'react';
import classes from './InstallInstructionsModal.module.scss';

// Two mobile flows — iOS and Android — plus a desktop fallback. Per-browser
// splits (Samsung, Firefox, Chromium-variants, in-app browsers) were dropped
// because the menu-and-add-to-home-screen action is functionally identical
// across every Android browser, and UA-based routing is fragile anyway.
type Platform = 'ios' | 'ios-non-safari' | 'android' | 'desktop';

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) {
    // Apple only allows Safari to install PWAs; Chrome/Firefox/Edge on iOS
    // hide the Add-to-Home-Screen action. Call that out explicitly.
    return /CriOS|FxiOS|EdgiOS/.test(ua) ? 'ios-non-safari' : 'ios';
  }
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

// iOS Safari share glyph — the one label users need to spot in the toolbar.
const ShareIcon = () => (
  <svg width="18" height="22" viewBox="0 0 24 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 2 L12 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M7 7 L12 2 L17 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M5 13 L5 26 A2 2 0 0 0 7 28 L17 28 A2 2 0 0 0 19 26 L19 13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="4" stroke="currentColor" strokeWidth="2" />
    <path d="M12 8 L12 16 M8 12 L16 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// The vertical-ellipsis / hamburger menu glyphs Android browsers use — drawn
// as SVG so we don't rely on Unicode ⋮/≡ being available at the right weight.
const MenuIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="5" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" />
  </svg>
);

type Step = { icon?: React.ReactNode; text: React.ReactNode };

function stepsFor(platform: Platform): { title: string; steps: Step[]; note?: React.ReactNode } {
  switch (platform) {
    case 'ios':
      return {
        title: 'Add Strand Services to your Home Screen',
        steps: [
          { icon: <ShareIcon />, text: <>Tap the <strong>Share</strong> button in the browser toolbar (the square with an up-arrow).</> },
          { icon: <PlusIcon />, text: <>Scroll down and tap <strong>Add to Home Screen</strong>.</> },
          { text: <>Tap <strong>Add</strong> in the top-right. Open Strand Services from your home screen from now on.</> },
        ],
        note: <>Push notifications on iPhone only work once the app is on your home screen. That's an Apple limitation.</>,
      };
    case 'ios-non-safari':
      return {
        title: 'Open this page in Safari first',
        steps: [
          { text: <>On iPhone and iPad, only <strong>Safari</strong> can add apps to the home screen. Chrome, Firefox and Edge on iOS can't.</> },
          { text: <>Copy this page's link, open <strong>Safari</strong>, paste it, then follow the <strong>Share → Add to Home Screen</strong> steps.</> },
        ],
      };
    case 'android':
      return {
        title: 'Add Strand Services to your Home screen',
        steps: [
          { icon: <MenuIcon />, text: <>Open your browser's menu (the <strong>⋮</strong> or <strong>≡</strong> button, usually top-right).</> },
          { text: <>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>, whichever your browser shows.</> },
          { text: <>Confirm. Strand Services will land on your home screen and open in its own window.</> },
        ],
        note: <>If you don't see either option, your browser doesn't support installing web apps. Chrome, Edge or Samsung Internet all do.</>,
      };
    case 'desktop':
    default:
      return {
        title: 'Install Strand Services on your computer',
        steps: [
          { text: <>Look for an <strong>install</strong> icon in your browser's address bar (a small monitor or download glyph).</> },
          { text: <>Or open the browser menu and choose <strong>Install Strand Services…</strong> / <strong>Add to Dock</strong> / <strong>Add to Home screen</strong>.</> },
        ],
        note: <>Firefox on desktop doesn't install web apps, but you can still enable notifications from the bell in the top bar.</>,
      };
  }
}

type Props = { onClose: () => void };

const InstallInstructionsModal: React.FC<Props> = ({ onClose }) => {
  const { title, steps, note } = stepsFor(detectPlatform());

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);

  return (
    <div className={classes.backdrop} onClick={onClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className={classes.close} onClick={onClose} aria-label="Close">✕</button>
        <h2 className={classes.title}>{title}</h2>
        <ol className={classes.steps}>
          {steps.map((s, i) => (
            <li key={i} className={classes.step}>
              <span className={classes.stepNumber}>{i + 1}</span>
              {s.icon && <span className={classes.stepIcon}>{s.icon}</span>}
              <span className={classes.stepText}>{s.text}</span>
            </li>
          ))}
        </ol>
        {note && <p className={classes.note}>{note}</p>}
        <button className={classes.done} onClick={onClose}>Got it</button>
      </div>
    </div>
  );
};

export default InstallInstructionsModal;
